import { EntitySearchType } from '@toggle/toggle';
import React, {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  ActionHandlers,
  ChatDocumentContextInput,
  ChatInputConfig,
} from '~/components/chat';
import { useDropdownKeyboardEvents } from '~/hooks/use-dropdown-keyboard-events/useDropdownKeyboardEvents';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { useTagsSearch } from '~/hooks/use-tags-search/useTagsSearch';
import { queryKeys } from '~/routes/query-params';
import { useChatInputStore } from '~/stores/use-chat-input/useChatInput';
import { Statistics } from '~/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';
import { useThemeSearch } from '~/widgets/search-asset-widget/hooks/use-theme-search/useThemeSearch';

import { ChatActionButtons } from '../../components/chat-action-buttons/ChatActionButtons';
import { ChatInputDropdown } from '../chat-input-dropdown/ChatInputDropdown';
import { UploadDisclaimer } from '../upload-disclaimer/UploadDisclaimer';
import * as S from './ChatInputComposer.styles';
import { isAssetNameMessage } from './utils';

export interface ChatInputComposerProps {
  className?: string;
  handlers: ActionHandlers;
  config: ChatInputConfig & {
    isLoading: boolean;
  };
  contextCounts?: {
    statistics?: Statistics;
    documentsCount: number;
    subTypeCount: number;
  };
}

const ChatInputComposerBase = forwardRef<
  HTMLTextAreaElement,
  ChatInputComposerProps
>(
  (
    { handlers, config, contextCounts, className }: ChatInputComposerProps,
    ref
  ) => {
    const { t } = useTranslation(['chat']);
    const { params } = useQueryParams();

    const isAssetMessage = useRef(false);
    const [inputValue, setInputValue] = useState('');
    const [showItems, setShowItems] = useState(true);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const { mode } = useChatNavigation();
    const { setHasUserInteracted, hasUserInteracted } = useChatInputStore(
      state => ({
        setHasUserInteracted: state.setHasUserInteracted,
        hasUserInteracted: state.hasUserInteracted,
      })
    );
    const dropdownApiRef = useDropdownKeyboardEvents();
    const { autoFocus: shouldAutoFocus = true } = config;

    // quickAnalysis -- enables asset search + quickAnalysis streaming API
    // quickAnalysis + research -- enables everything, only on dashboard
    // empty -- only question, quickAnalysis streaming API
    const allowedModes = config.allowedModes ?? ['quickAnalysis'];
    const enableAssetSearch =
      mode === 'quickAnalysis' && allowedModes.includes('quickAnalysis');
    const enableThemeSearch =
      enableAssetSearch && !!(config.enableThemeSearch ?? true);

    const {
      tags: tagList,
      searchTags,
      clearTags,
    } = useTagsSearch({
      searchType: EntitySearchType.Entity,
      additionalConditionChecker: isAssetNameMessage,
    });
    const { themeSearchResults, handleTextChange } = useThemeSearch(
      false,
      enableThemeSearch
    );

    useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
      ref,
      () => inputRef.current,
      [inputRef]
    );

    const clearInput = () => {
      setInputValue('');
    };

    const clearDropdown = () => {
      clearTags();
      setShowItems(false);

      if (!config?.persistInputTextOnSubmit) {
        setInputValue('');
      }
    };

    const onSubmitCallbackHandler = () => {
      if (!config?.persistInputTextOnSubmit) {
        clearInput();
      }

      clearDropdown();

      if (inputRef.current) {
        inputRef.current.rows = 1;
      }
    };

    const onChatSubmit = (
      event:
        | React.FormEvent<HTMLFormElement>
        | React.KeyboardEvent<HTMLTextAreaElement>
    ) => {
      event.stopPropagation();
      clearInput();
      handlers.onSubmit(event, inputValue, onSubmitCallbackHandler);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      isAssetMessage.current = isAssetNameMessage(value);
      setInputValue(value);
      setHasUserInteracted(true);
      setShowItems(true);

      enableAssetSearch && searchTags(value);
      enableThemeSearch && handleTextChange(value);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        if (dropdownApiRef.current?.focusedItemIndex === -1) {
          event.stopPropagation();
          onChatSubmit(event);
        }
      }
    };

    useEffect(() => {
      if (inputRef.current && shouldAutoFocus) {
        inputRef.current.focus();
      } else {
        clearInput();
      }
    }, [inputRef, params.mode, params.conversationId, shouldAutoFocus]);

    useEffect(() => {
      if (!hasUserInteracted) {
        setInputValue(config.suggestedValue ?? '');

        if (inputRef.current && !!params[queryKeys.activeDrawer]) {
          inputRef.current.scrollTop = 0;
        }
      }
    }, [config.suggestedValue, hasUserInteracted, inputValue, params]);

    const placeholderText = allowedModes.length
      ? t('chat:placeholder', { context: mode })
      : undefined;

    return (
      <S.Form onSubmit={onChatSubmit} className={className}>
        <S.LoaderWrapper $isMessagePending={config.isLoading}>
          <ChatInputDropdown
            config={config}
            handlers={{ ...handlers, onSubmitCallbackHandler }}
            inputValue={inputValue}
            tagList={tagList}
            themesList={themeSearchResults}
            clearDropdown={clearDropdown}
            isAssetMessage={isAssetMessage}
            showItems={showItems}
            setInputValue={setInputValue}
            dropdownApiRef={dropdownApiRef}
          >
            <ChatDocumentContextInput
              inputRef={inputRef}
              data-testid="chat-input"
              autoFocus={shouldAutoFocus}
              placeholder={config?.placeholder ?? placeholderText}
              value={inputValue}
              onChange={onInputChange}
              disabled={config.isCustomizedDisabled}
              isMessagePending={config.isLoading}
              contextCounts={contextCounts}
              onKeyDownProp={e => {
                onKeyDown(e);
              }}
              allowedModes={allowedModes}
            >
              <ChatActionButtons
                inputValue={inputValue}
                config={config}
                isMessagePending={config.isLoading}
                abortRequest={handlers.onAbort}
              />
            </ChatDocumentContextInput>
          </ChatInputDropdown>
        </S.LoaderWrapper>
        <S.Footer>
          <UploadDisclaimer />
        </S.Footer>
      </S.Form>
    );
  }
);

export const ChatInputComposer: ForwardRefExoticComponent<
  ChatInputComposerProps & RefAttributes<HTMLTextAreaElement>
> & {
  Styled: typeof S;
} = Object.assign(ChatInputComposerBase, { Styled: S });
